/**
 * Build provenance — "which commit is this bundle, and which commit is the
 * server it is talking to?".
 *
 * WHY THIS EXISTS. Served assets are content-hashed with no commit stamp, so a
 * deploy could not be tied back to a git SHA from the edge: verification had to
 * infer it from bundle-hash movement and sourcemap probes. Both halves are now
 * stamped — this module is the frontend half.
 *
 * WHERE THE VALUE COMES FROM. `__ELA_BUILD_SHA__` is replaced TEXTUALLY by
 * Vite's `define` at build time with the commit resolved in `vite.config.ts`
 * (the same order the Zig `build.zig` uses: explicit override → git working tree
 * → `CI_COMMIT_SHA` → `unknown`). There is deliberately no constant here for
 * anyone to forget to bump, and the `package.json` version — which nobody bumps
 * — is never consulted. The identical string is also stamped into `index.html`
 * as `<meta name="ela-build">`, so one `curl` identifies the served bundle.
 *
 * `unknown` IS A REAL STATE. A build with no git, no CI env and no override says
 * so, and the UI renders it as visibly wrong rather than as a plausible version.
 * This codebase has shipped values that looked authoritative and were not
 * (`anchor_state` defaulting to `"exact"`; a ticket queue printing "Anchored"
 * for an unanchored comment) — a version banner is exactly that shape of risk,
 * so the honest-unknown path is the one that is tested hardest.
 */

/**
 * The single spelling of "this build does not know its commit", shared with
 * `build.zig`'s `unknown_sha` and `src/build_stamp.zig`'s `unknown`. A second
 * spelling anywhere means one layer renders the sentinel as if it were a
 * version.
 */
export const UNKNOWN_SHA = "unknown";

/** Injected by Vite `define`. Undeclared under `bun test` — `typeof` is safe. */
declare const __ELA_BUILD_SHA__: string | undefined;

/**
 * Coerce whatever the build (or the server) handed us into either a real commit
 * name or the sentinel. Pure, so the whole policy is unit-testable without a
 * build step.
 *
 * Rejected — because each of these READS as a version while carrying no
 * provenance: empty/whitespace, non-strings, the literal `undefined`/`null`
 * text, and anything that is not a hexadecimal git object name (optionally
 * suffixed `-dirty`). A semver, a branch name or a friendly word therefore
 * degrades to `unknown` instead of being displayed as a commit.
 */
export function normalizeSha(raw: unknown): string {
  if (typeof raw !== "string") return UNKNOWN_SHA;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return UNKNOWN_SHA;
  if (trimmed === "undefined" || trimmed === "null") return UNKNOWN_SHA;
  if (trimmed === UNKNOWN_SHA) return UNKNOWN_SHA;
  const body = trimmed.endsWith("-dirty")
    ? trimmed.slice(0, -"-dirty".length)
    : trimmed;
  if (!/^[0-9a-f]{7,40}$/i.test(body)) return UNKNOWN_SHA;
  return trimmed;
}

/** True when `sha` names a real commit rather than the sentinel. */
export function isKnownSha(sha: string): boolean {
  return sha.length > 0 && sha !== UNKNOWN_SHA;
}

/**
 * True when the build carried uncommitted changes to tracked files, so the
 * artefact is NOT reproducible from the commit alone. Surfaced in the UI: a
 * dirty build that silently reads as its base commit is a lie about what ran.
 */
export function isDirtySha(sha: string): boolean {
  return isKnownSha(sha) && sha.endsWith("-dirty");
}

/**
 * Display form: the first 7 hex digits, keeping any `-dirty` qualifier. `git
 * log --oneline` length — long enough to identify, short enough for a 208px
 * popover. The full value stays available for the `title`.
 */
export function shortSha(sha: string): string {
  if (!isKnownSha(sha)) return UNKNOWN_SHA;
  const dirty = isDirtySha(sha);
  const body = dirty ? sha.slice(0, -"-dirty".length) : sha;
  return dirty ? `${body.slice(0, 7)}-dirty` : body.slice(0, 7);
}

/**
 * The commit THIS bundle was built from. A function, not a module constant, so
 * the textual `define` substitution happens where it is read and the value can
 * never be captured at import time by a test that then asserts against itself.
 */
export function frontendSha(): string {
  return normalizeSha(
    typeof __ELA_BUILD_SHA__ === "string" ? __ELA_BUILD_SHA__ : undefined,
  );
}
